import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineX } from "react-icons/hi";

import StudentAvatar from "../TaggedStudentPanel/StudentAvatar";
import type { Student } from "types/Student";

const POPOVER_WIDTH = 320;
// Tall enough to fit ~6 students before scrolling (header + search + 6 rows).
const POPOVER_MAX_HEIGHT = 380;
const EDGE_GAP = 8;
const POINT_GAP = 14;

interface TagStudentPopoverProps {
  containerRef: React.RefObject<HTMLDivElement>;
  pictureDimensions: { width: number; height: number };
  transformState: { scale: number; positionX: number; positionY: number };
  /** Click position in source-image pixels. */
  pendingCoordinates: { x: number; y: number } | null;
  /** Students that can still be tagged on this photo. */
  availableStudents: Student[];
  awaitingStudentId: number | null;
  onSelect: (studentId: number) => void;
  onClose: () => void;
}

/**
 * Roster shown directly over the photo at the point that was clicked, so the
 * teacher picks the child right where they are looking instead of moving to
 * the side panel.
 */
const TagStudentPopover: React.FC<TagStudentPopoverProps> = ({
  containerRef,
  pictureDimensions,
  transformState,
  pendingCoordinates,
  availableStudents,
  awaitingStudentId,
  onSelect,
  onClose,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Reset the query each time a new point is picked.
  useEffect(() => {
    if (!pendingCoordinates) return;
    setSearch("");
    const raf = requestAnimationFrame(() => searchInputRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [pendingCoordinates]);

  useEffect(() => {
    if (!pendingCoordinates) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [pendingCoordinates, onClose]);

  // Same projection the markers use, so the popover anchors exactly to the dot.
  const position = useMemo(() => {
    const container = containerRef.current;
    if (
      !pendingCoordinates ||
      !container ||
      pictureDimensions.width === 0 ||
      pictureDimensions.height === 0
    ) {
      return null;
    }

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const aspectRatio = pictureDimensions.width / pictureDimensions.height;
    const containerAspectRatio = containerWidth / containerHeight;

    let imageDisplayWidth: number;
    let imageDisplayHeight: number;
    let offsetX: number;
    let offsetY: number;

    if (aspectRatio > containerAspectRatio) {
      imageDisplayWidth = containerWidth;
      imageDisplayHeight = containerWidth / aspectRatio;
      offsetX = 0;
      offsetY = (containerHeight - imageDisplayHeight) / 2;
    } else {
      imageDisplayWidth = containerHeight * aspectRatio;
      imageDisplayHeight = containerHeight;
      offsetX = (containerWidth - imageDisplayWidth) / 2;
      offsetY = 0;
    }

    const { scale = 1, positionX = 0, positionY = 0 } = transformState;
    const pointX =
      (pendingCoordinates.x / pictureDimensions.width) *
        imageDisplayWidth *
        scale +
      positionX +
      offsetX;
    const pointY =
      (pendingCoordinates.y / pictureDimensions.height) *
        imageDisplayHeight *
        scale +
      positionY +
      offsetY;

    const clamp = (value: number, min: number, max: number) =>
      Math.min(Math.max(value, min), max);

    // Prefer right/below the point; flip when it would overflow the photo.
    const overflowsRight =
      pointX + POINT_GAP + POPOVER_WIDTH > containerWidth - EDGE_GAP;
    const left = clamp(
      overflowsRight ? pointX - POINT_GAP - POPOVER_WIDTH : pointX + POINT_GAP,
      EDGE_GAP,
      Math.max(EDGE_GAP, containerWidth - POPOVER_WIDTH - EDGE_GAP),
    );

    const overflowsBottom =
      pointY + POINT_GAP + POPOVER_MAX_HEIGHT > containerHeight - EDGE_GAP;
    const top = clamp(
      overflowsBottom
        ? pointY - POINT_GAP - POPOVER_MAX_HEIGHT
        : pointY + POINT_GAP,
      EDGE_GAP,
      Math.max(EDGE_GAP, containerHeight - POPOVER_MAX_HEIGHT - EDGE_GAP),
    );

    return { left, top };
  }, [
    containerRef,
    pendingCoordinates,
    pictureDimensions.height,
    pictureDimensions.width,
    transformState,
  ]);

  const filteredStudents = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return availableStudents;
    return availableStudents.filter((student) => {
      const tokens = [
        `${student.last_name} ${student.first_name}`,
        `${student.first_name} ${student.last_name}`,
        `${student.last_name_kata ?? ""} ${student.first_name_kata ?? ""}`,
        `${student.id}`,
      ];
      return tokens.some((token) =>
        token.toLowerCase().replace(/\s+/g, " ").trim().includes(query),
      );
    });
  }, [availableStudents, search]);

  if (!pendingCoordinates || !position) return null;

  return (
    <div
      data-tag-ignore-click
      role="dialog"
      aria-label={t("_tagged_student_panel._title", {
        defaultValue: "Tag a student",
      })}
      className="pointer-events-auto absolute z-40 flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl"
      style={{
        left: position.left,
        top: position.top,
        width: POPOVER_WIDTH,
        maxHeight: POPOVER_MAX_HEIGHT,
      }}
    >
      <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-2">
        <p className="text-xs font-semibold text-gray-900">
          {t("_tagged_student_panel._select_student_to_tag", {
            defaultValue: "Select the student to add a tag.",
          })}
        </p>
        <button
          type="button"
          data-tag-ignore-click
          onClick={onClose}
          aria-label={t("_generic._close", { defaultValue: "Close" })}
          className="rounded-full p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-emerald-200"
        >
          <HiOutlineX className="h-4 w-4" />
        </button>
      </div>

      <div className="px-3 py-2">
        <input
          ref={searchInputRef}
          type="text"
          value={search}
          data-tag-ignore-click
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t("_tagged_student_panel._search_placeholder", {
            defaultValue: "Search students…",
          })}
          className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {filteredStudents.length === 0 ? (
          <p className="px-2 py-4 text-center text-xs text-gray-500">
            {t("_generic._no_data", { defaultValue: "No students found" })}
          </p>
        ) : (
          <ul className="space-y-1">
            {filteredStudents.map((student) => {
              const displayName =
                `${student.last_name} ${student.first_name}`.trim();
              const kanaName = `${student.last_name_kata ?? ""} ${
                student.first_name_kata ?? ""
              }`.trim();
              const isSubmitting = awaitingStudentId === student.id;

              return (
                <li key={student.id}>
                  <button
                    type="button"
                    data-tag-ignore-click
                    disabled={awaitingStudentId !== null}
                    onClick={() => onSelect(student.id)}
                    aria-label={t("_tagged_student_panel._tag_student", {
                      studentName: displayName,
                      defaultValue: "Tag {{studentName}}",
                    })}
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <StudentAvatar student={student} size="sm" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-gray-900">
                        {displayName}
                      </span>
                      {kanaName && (
                        <span className="block truncate text-xs text-gray-500">
                          {kanaName}
                        </span>
                      )}
                    </span>
                    {isSubmitting && (
                      <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-emerald-300 border-t-transparent" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default TagStudentPopover;
